import React, { useCallback, useEffect, useMemo } from 'react';
import { MdBarChart } from 'react-icons/md';

import OptimizationPipelineSidebarTable from 'app/admin/optimizationPipelineV2/component/pipeline/sidebars/base/OptimizationPipelineSidebarTable';
import { SidebarExportButtons, SidebarHeaderSearch, SidebarHeaderCheckbox } from 'app/admin/optimizationPipelineV2/component/pipeline/sidebars/base/OptimizationPipelineSidebar';
import { useOptimizationPipeline } from 'app/admin/optimizationPipelineV2/component/pipeline/providers/OptimizationPipelineProvider';
import { exportSidebarTable, buildExportFilename } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/exportUtils';

const POPUP_TYPE = 'MODEL_VALIDATION_DETAILS_CHART';
const FIELD_HINTS = new Set(['value', 'true_value', 'estimated_value', 'ae', 'se', 'ape', 'tolerance', 'accurate']);

const MODEL_VALIDATION_TABLE_CONFIG = {
    columns: [
        {
            key: 'variable',
            label: 'Attribute',
            sortable: true,
            searchable: true,
            align: 'left',
            minWidth: 320,
            maxWidth: 420,
            truncate: true,
            tooltip: true,
            cellClassName: 'whitespace-nowrap'
        },
        {
            key: 'value',
            label: 'Value',
            sortable: true,
            searchable: true,
            align: 'right',
            maxWidth: 120,
            numeric: true,
            cellClassName: 'whitespace-nowrap tabular-nums'
        },
        {
            key: 'uom',
            label: 'UOM',
            sortable: true,
            searchable: true,
            align: 'right',
            maxWidth: 100,
            cellClassName: 'whitespace-nowrap'
        },
        {
            key: 'true_value',
            label: 'True',
            sortable: true,
            searchable: true,
            align: 'right',
            maxWidth: 120,
            numeric: true,
            cellClassName: 'whitespace-nowrap tabular-nums'
        },
        {
            key: 'estimated_value',
            label: 'Est',
            sortable: true,
            searchable: true,
            align: 'right',
            maxWidth: 120,
            numeric: true,
            cellClassName: 'whitespace-nowrap tabular-nums'
        },
        {
            key: 'ae',
            label: 'Ae',
            sortable: true,
            searchable: true,
            align: 'right',
            maxWidth: 110,
            numeric: true,
            cellClassName: 'whitespace-nowrap tabular-nums'
        },
        {
            key: 'se',
            label: 'Se',
            sortable: true,
            searchable: true,
            align: 'right',
            maxWidth: 110,
            numeric: true,
            cellClassName: 'whitespace-nowrap tabular-nums'
        },
        {
            key: 'ape',
            label: 'Ape',
            sortable: true,
            searchable: true,
            align: 'right',
            maxWidth: 110,
            numeric: true,
            cellClassName: 'whitespace-nowrap tabular-nums'
        },
        {
            key: 'tolerance',
            label: 'Tol',
            sortable: true,
            searchable: true,
            align: 'right',
            maxWidth: 110,
            numeric: true,
            cellClassName: 'whitespace-nowrap tabular-nums'
        },
        {
            key: 'accurate',
            label: 'Acc',
            sortable: true,
            searchable: true,
            align: 'right',
            maxWidth: 90,
            cellClassName: 'whitespace-nowrap'
        },
        {
            key: 'history',
            label: '',
            sortable: false,
            searchable: false,
            align: 'center',
            width: 64,
            minWidth: 64,
            maxWidth: 64,
            exportable: false
        }
    ],
    search: {
        enabled: true,
        showInput: false,
        debounceMs: 250,
        placeholder: 'Search model validation...'
    },
    sort: {
        key: 'variable',
        direction: 'asc'
    },
    states: {
        loadingTitle: 'Loading model validation...',
        emptyTitle: 'No model validation data available.',
        noResultsTitle: 'No matching model validation rows found.',
        errorTitle: 'Failed to load model validation',
        errorDescription: 'Please try again later.'
    }
};

function isPrimitive(value) {
    return value == null || typeof value !== 'object';
}

function looksLikeFieldSet(obj) {
    return !!obj && typeof obj === 'object' && Object.keys(obj).some((key) => FIELD_HINTS.has(key));
}

function rootFromPath(path) {
    return String(path || '').split(/[.\[]/)[0];
}

function parseComponents(components) {
    if (Array.isArray(components)) return components;
    if (components && typeof components === 'object') return Object.values(components);
    if (!components || typeof components !== 'string') return [];

    try {
        const parsed = JSON.parse(components);
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        return [];
    }
}

function buildUomMap(cleanMeasurementData, mergedMeasurementData) {
    const uomMap = new Map();
    const fields = [
        ...(Array.isArray(mergedMeasurementData?.fields) ? mergedMeasurementData.fields : []),
        ...(Array.isArray(cleanMeasurementData?.fields) ? cleanMeasurementData.fields : [])
    ];

    fields.forEach((field) => {
        if (!field?.name) return;

        const name = String(field.name);
        uomMap.set(name, field.uom ?? null);

        parseComponents(field.components).forEach((component) => {
            if (!component?.name) return;
            uomMap.set(name + '.' + String(component.name), field.uom ?? null);
        });
    });

    return uomMap;
}

function buildRowsFromFeatures(features, uomMap) {
    const rows = [];
    const hasUoms = !!uomMap && typeof uomMap.get === 'function' && uomMap.size > 0;

    function getUom(path) {
        if (!hasUoms) return undefined;

        const normalizedPath = String(path || '');

        if (uomMap.has(normalizedPath)) {
            return uomMap.get(normalizedPath) ?? null;
        }

        return uomMap.get(rootFromPath(normalizedPath)) ?? null;
    }

    function walk(prefix, value) {
        if (isPrimitive(value)) {
            const uom = getUom(prefix);

            rows.push({
                variable: prefix,
                fields: hasUoms ? { value, uom } : { value }
            });

            return;
        }

        if (Array.isArray(value)) {
            value.forEach((item, index) => {
                walk(prefix + '[' + index + ']', item);
            });

            return;
        }

        if (looksLikeFieldSet(value)) {
            const uom = getUom(prefix);

            rows.push({
                variable: prefix,
                fields: hasUoms ? { ...value, uom } : value
            });

            return;
        }

        Object.entries(value || {}).forEach(([key, nextValue]) => {
            walk(prefix ? prefix + '.' + key : key, nextValue);
        });
    }

    Object.entries(features || {}).forEach(([key, value]) => {
        walk(key, value);
    });

    return rows;
}

function buildColumns(rows) {
    const known = ['variable', 'value', 'uom', 'true_value', 'estimated_value', 'ae', 'se', 'ape', 'tolerance', 'accurate'];
    const present = new Set();

    rows.forEach((row) => {
        Object.keys(row.fields || {}).forEach((column) => {
            present.add(column);
        });
    });

    const others = Array.from(present)
        .filter((column) => !known.includes(column))
        .sort((a, b) => a.localeCompare(b));

    return ['variable', ...known.filter((column) => column !== 'variable' && present.has(column)), ...others, 'history'];
}

function buildTableConfig(detectedColumns) {
    const enabledColumnKeys = detectedColumns.filter((column) => column !== 'history');

    return {
        ...MODEL_VALIDATION_TABLE_CONFIG,
        columns: MODEL_VALIDATION_TABLE_CONFIG.columns.filter((column) => enabledColumnKeys.includes(column.key) || column.key === 'history')
    };
}

function fmt(value) {
    if (value == null || Number.isNaN(value)) return '-';

    if (typeof value === 'number') {
        const abs = Math.abs(value);

        if (abs >= 100) return value.toFixed(1);
        if (abs >= 1) return value.toFixed(2);

        return value.toFixed(3);
    }

    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }

    return String(value);
}

function truncateUom(uom, max = 5, head = 3) {
    const stringValue = uom == null ? '' : String(uom);

    if (!stringValue) return '-';
    if (stringValue.length <= max) return stringValue;

    return stringValue.slice(0, head) + '...';
}

function hasTolerance(fields) {
    return fields && Object.prototype.hasOwnProperty.call(fields, 'tolerance') && fields.tolerance != null;
}

function getRowColor(fields) {
    if (fields?.accurate === true) return 'green';
    if (fields?.accurate === false) return 'red';
    return null;
}

function buildTableRows(rows, columns, onOpenTimeseries) {
    return rows.map((row) => {
        const cells = {
            variable: {
                value: row.variable,
                tooltip: row.variable
            },
            history: {
                component: typeof onOpenTimeseries === 'function' ? (
                    <button
                        type='button'
                        className='w-28 h-28 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors duration-200'
                        title='Open timeseries'
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onOpenTimeseries([row.variable]);
                        }}
                    >
                        <MdBarChart className='text-16 text-gray-600' />
                    </button>
                ) : null
            }
        };

        columns.forEach((column) => {
            if (column === 'variable' || column === 'history') return;

            const rawValue = row.fields?.[column];

            if (column === 'uom') {
                const fullUom = rawValue == null ? '' : String(rawValue);

                cells[column] = {
                    value: fullUom || '-',
                    displayValue: truncateUom(fullUom),
                    exportValue: fullUom || '-',
                    tooltip: fullUom && fullUom.length > 5 ? fullUom : undefined
                };

                return;
            }

            cells[column] = {
                value: rawValue,
                displayValue: fmt(rawValue)
            };
        });

        return {
            id: row.variable,
            rowColor: getRowColor(row.fields),
            searchableText: row.variable + ' ' + Object.values(row.fields || {}).map((value) => String(value)).join(' '),
            cells
        };
    });
}

export function ModelValidationSidebarHeaderActions() {
    const { sidebar, openSidebar, openPopup, optimizationPipelineDetail, getOnDemandResource, ts } = useOptimizationPipeline();

    const query = sidebar?.query || '';
    const relevantOnly = !!sidebar?.relevantOnly;
    const relevantOnlyId = 'model-validation-relevant-only';

    const features = optimizationPipelineDetail?.model_validation_data?.features || null;
    const cleanMeasurementData = getOnDemandResource('cleanMeasurementData')?.data;
    const mergedMeasurementData = getOnDemandResource('mergedMeasurementData')?.data;

    const uomMap = useMemo(() => {
        return buildUomMap(cleanMeasurementData, mergedMeasurementData);
    }, [cleanMeasurementData, mergedMeasurementData]);

    const availableKeys = useMemo(() => {
        const keys = features ? Object.keys(features).filter(Boolean) : [];
        return keys.sort((a, b) => a.localeCompare(b, 'hu'));
    }, [features]);

    const rowsAll = useMemo(() => {
        return buildRowsFromFeatures(features, uomMap);
    }, [features, uomMap]);

    const relevantKeys = useMemo(() => {
        return rowsAll
            .filter((row) => hasTolerance(row.fields || {}))
            .map((row) => row.variable)
            .filter(Boolean);
    }, [rowsAll]);

    const detectedColumns = useMemo(() => {
        return buildColumns(rowsAll);
    }, [rowsAll]);

    const filteredRows = useMemo(() => {
        if (!relevantOnly) return rowsAll;
        return rowsAll.filter((row) => hasTolerance(row.fields || {}));
    }, [rowsAll, relevantOnly]);

    const tableConfig = useMemo(() => {
        return buildTableConfig(detectedColumns);
    }, [detectedColumns]);

    const exportRows = useMemo(() => {
        return buildTableRows(filteredRows, detectedColumns, null);
    }, [filteredRows, detectedColumns]);

    const headerSeedKeys = relevantKeys.length ? relevantKeys : availableKeys;

    function handleExport(format) {
        exportSidebarTable({
            tableConfig,
            rows: exportRows,
            query,
            sort: sidebar?.sort,
            filename: buildExportFilename('model-validation', ts),
            format
        });
    }

    return (
        <div className='flex items-center gap-8'>
            <SidebarExportButtons onExcel={() => handleExport('excel')} onCsv={() => handleExport('csv')} disabled={!exportRows.length} />

            <button
                type='button'
                className='w-28 h-28 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors duration-200'
                title='Open feature timeseries'
                disabled={!availableKeys.length}
                onClick={() => {
                    openPopup({
                        type: POPUP_TYPE,
                        requestToken: Date.now(),
                        availableKeys: availableKeys,
                        defaultSelectedKeys: headerSeedKeys,
                        uomMap: uomMap
                    });
                }}
            >
                <MdBarChart className='text-16 text-gray-700' />
            </button>

            <SidebarHeaderSearch
                value={query}
                onChange={(nextQuery) => { openSidebar({ ...sidebar, query: nextQuery }); }}
            />

            <SidebarHeaderCheckbox
                id={relevantOnlyId}
                label='Show relevant'
                checked={relevantOnly}
                onChange={(checked) => { openSidebar({ ...sidebar, relevantOnly: checked }); }}
            />

        </div>
    );
}

export default function ModelValidationSidebar() {
    const {
        sidebar,
        openSidebar,
        optimizationPipelineDetail,
        openPopup,
        getOnDemandResource,
        ensureOnDemandResource
    } = useOptimizationPipeline();

    const relevantOnly = !!sidebar?.relevantOnly;

    const features = optimizationPipelineDetail?.model_validation_data?.features || null;
    const cleanMeasurementData = getOnDemandResource('cleanMeasurementData')?.data;
    const mergedMeasurementData = getOnDemandResource('mergedMeasurementData')?.data;

    useEffect(() => {
        ensureOnDemandResource('cleanMeasurementData');
        ensureOnDemandResource('mergedMeasurementData');
    }, [ensureOnDemandResource]);

    const uomMap = useMemo(() => {
        return buildUomMap(cleanMeasurementData, mergedMeasurementData);
    }, [cleanMeasurementData, mergedMeasurementData]);

    const availableKeys = useMemo(() => {
        const keys = features ? Object.keys(features).filter(Boolean) : [];
        return keys.sort((a, b) => a.localeCompare(b, 'hu'));
    }, [features]);

    const rowsAll = useMemo(() => {
        return buildRowsFromFeatures(features, uomMap);
    }, [features, uomMap]);

    const relevantKeys = useMemo(() => {
        return rowsAll
            .filter((row) => hasTolerance(row.fields || {}))
            .map((row) => row.variable)
            .filter(Boolean);
    }, [rowsAll]);

    const detectedColumns = useMemo(() => {
        return buildColumns(rowsAll);
    }, [rowsAll]);

    const filteredRows = useMemo(() => {
        if (!relevantOnly) return rowsAll;
        return rowsAll.filter((row) => hasTolerance(row.fields || {}));
    }, [rowsAll, relevantOnly]);

    const openTimeseries = useCallback((seedKeys) => {
        const resolvedSeedKeys = Array.isArray(seedKeys) && seedKeys.length
            ? seedKeys
            : (relevantKeys.length ? relevantKeys : availableKeys);

        openPopup({
            type: POPUP_TYPE,
            requestToken: Date.now(),
            availableKeys: availableKeys,
            defaultSelectedKeys: resolvedSeedKeys,
            uomMap: uomMap
        });
    }, [availableKeys, relevantKeys, openPopup, uomMap]);

    useEffect(() => {
        if (!sidebar?.historyRequestToken) return;

        openTimeseries(Array.isArray(sidebar.historySeedKeys) ? sidebar.historySeedKeys : []);
    }, [sidebar?.historyRequestToken, sidebar?.historySeedKeys, openTimeseries]);

    const tableConfig = useMemo(() => {
        return buildTableConfig(detectedColumns);
    }, [detectedColumns]);

    const tableRows = useMemo(() => {
        return buildTableRows(filteredRows, detectedColumns, openTimeseries);
    }, [filteredRows, detectedColumns, openTimeseries]);

    const loading = !optimizationPipelineDetail?.model_validation_data?.features;

    return (
        <div className='w-full h-full min-h-0 flex flex-col overflow-hidden'>
            <div className='w-full flex-1 min-h-0 overflow-hidden'>
                <OptimizationPipelineSidebarTable
                    tableConfig={tableConfig}
                    rows={tableRows}
                    loading={loading}
                    query={sidebar?.query || ''}
                    sort={sidebar?.sort}
                    onQueryChange={(nextQuery) => {
                        openSidebar({ ...sidebar, query: nextQuery });
                    }}
                    onSortChange={(nextSort) => {
                        openSidebar({ ...sidebar, sort: nextSort });
                    }}
                />
            </div>
        </div>
    );
}
